import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {refreshToken, postAuthLogout, getUserByToken} from './_requests'
import {WithChildren} from '../../../../_metronic/helpers'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = async () => {
    try {
      await postAuthLogout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      saveAuth(undefined)
      setCurrentUser(undefined)
    }
  }

  return (
    <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, logout}}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, saveAuth, logout, setCurrentUser} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  
  // On app initialization, try to refresh the token if we have auth stored
  useEffect(() => {
    const initAuth = async () => {
      try {
        if (!didRequest.current) {
          didRequest.current = true
          
          if (auth && auth.accessToken) {
            // Try to refresh the token
            const {data: newAuth} = await refreshToken()
            
            if (newAuth && newAuth.accessToken) {
              // Update auth with new tokens
              saveAuth(newAuth)

              // Fetch full user profile including accessPermissions
              try {
                const {data: fullUser} = await getUserByToken(newAuth.accessToken)
                if (fullUser) {
                  setCurrentUser(fullUser)
                  authHelper.setUser(fullUser)
                }
              } catch {
                // Fall back to stored user if profile fetch fails
                const storedUser = authHelper.getUser()
                if (storedUser) {
                  setCurrentUser(storedUser)
                }
              }
            } else {
              // Refresh failed, logout
              logout()
            }
          } else {
            // No auth stored, logout
            logout()
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        if (!didRequest.current) {
          didRequest.current = true
        }
        logout()
      } finally {
        setShowSplashScreen(false)
      }
    }

    if (auth && auth.accessToken) {
      initAuth()
    } else {
      setShowSplashScreen(false)
    }
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
